
import { supabase } from '@/integrations/supabase/client';
import { PlayerRegistrationStatus, RegistrationStatus, TeamAssignmentStatus, PlayerAvailabilityStatus } from '@/types/database';

export class PlayerRegistrationService {
  // Get player registration status for a specific team
  static async getPlayerRegistrationStatus(userId: string, teamId: number): Promise<PlayerRegistrationStatus | null> {
    const { data, error } = await supabase
      .from('player_registration_status')
      .select(`
        *,
        team:teams(*)
      `)
      .eq('user_id', userId)
      .eq('team_id', teamId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching player registration status:', error);
      throw error;
    }

    return data;
  }

  // Get all registration statuses for a player
  static async getPlayerRegistrations(userId: string): Promise<PlayerRegistrationStatus[]> {
    const { data, error } = await supabase
      .from('player_registration_status')
      .select(`
        *,
        team:teams(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching player registrations:', error);
      throw error;
    }

    return data || [];
  }

  // Get all registrations for a team
  static async getTeamRegistrations(teamId: number): Promise<PlayerRegistrationStatus[]> {
    const { data, error } = await supabase
      .from('player_registration_status')
      .select(`
        *,
        team:teams(*)
      `)
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching team registrations:', error);
      throw error;
    }

    return data || [];
  }

  // Create a new player registration
  static async createPlayerRegistration(
    userId: string,
    teamId: number,
    registrationStatus: RegistrationStatus = 'open'
  ): Promise<PlayerRegistrationStatus> {
    const { data, error } = await supabase
      .from('player_registration_status')
      .insert({
        user_id: userId,
        team_id: teamId,
        registration_status: registrationStatus,
        team_assignment_status: 'unassigned',
        availability_status: 'active'
      })
      .select(`
        *,
        team:teams(*)
      `)
      .single();

    if (error) {
      console.error('Error creating player registration:', error);
      throw error;
    }

    return data;
  }

  // Update registration status
  static async updateRegistrationStatus(
    registrationId: string,
    updates: {
      registration_status?: RegistrationStatus;
      team_assignment_status?: TeamAssignmentStatus;
      availability_status?: PlayerAvailabilityStatus;
      assigned_team_name?: string;
      status_notes?: string;
      status_updated_by?: string;
    }
  ): Promise<PlayerRegistrationStatus> {
    const { data, error } = await supabase
      .from('player_registration_status')
      .update({
        ...updates,
        status_updated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', registrationId)
      .select(`
        *,
        team:teams(*)
      `)
      .single();

    if (error) {
      console.error('Error updating registration status:', error);
      throw error;
    }

    return data;
  }

  // Get registration statistics for a team
  static async getTeamRegistrationStats(teamId: number) {
    const { data, error } = await supabase
      .from('player_registration_status')
      .select('registration_status, team_assignment_status, availability_status')
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team registration stats:', error);
      throw error;
    }

    const stats = {
      total: data?.length || 0,
      registrationStatus: {
        open: 0,
        closed: 0,
        waitlisted: 0,
        pending_approval: 0,
        approved: 0,
        declined: 0
      },
      teamAssignment: {
        unassigned: 0,
        assigned: 0,
        roster_open: 0,
        roster_locked: 0
      },
      availability: {
        active: 0,
        inactive: 0,
        injured: 0,
        doubtful: 0,
        questionable: 0,
        probable: 0,
        out: 0,
        suspended: 0,
        pup: 0,
        eligible_verified: 0,
        play_up_request: 0
      }
    };

    data?.forEach(row => {
      if (row.registration_status) {
        stats.registrationStatus[row.registration_status]++;
      }
      if (row.team_assignment_status) {
        stats.teamAssignment[row.team_assignment_status]++;
      }
      if (row.availability_status) {
        stats.availability[row.availability_status]++;
      }
    });

    return stats;
  }
}

export const playerRegistrationService = PlayerRegistrationService;
